import React, { useState } from 'react';
import { FiDownload, FiX, FiCalendar, FiFileText } from 'react-icons/fi';
import './DownloadReportModal.scss';

const DownloadReportModal = ({
  isOpen,
  onClose,
  onDownload,
  title = 'Descargar Reporte',
  moduleName = 'reporte',
  isLoading = false
}) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [format, setFormat] = useState('pdf');
  // Por defecto se descargan TODOS los registros: con 'range' preseleccionado
  // el usuario elegía un rango reciente y en módulos de catálogo (productos,
  // clientes, proveedores) el filtro por fecha de creación dejaba el archivo vacío.
  const [dateFilter, setDateFilter] = useState('all'); // 'range' o 'all'
  const [errors, setErrors] = useState({});

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};

    // Solo validar fechas si se seleccionó filtro por rango
    if (dateFilter === 'range') {
      if (!startDate) {
        newErrors.startDate = 'Fecha de inicio es requerida';
      }

      if (!endDate) {
        newErrors.endDate = 'Fecha de fin es requerida';
      }

      if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
        newErrors.dateRange = 'La fecha de inicio no puede ser mayor que la fecha de fin';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar descarga
  const handleDownload = () => {
    if (validateForm()) {
      onDownload({
        startDate: dateFilter === 'all' ? null : startDate,
        endDate: dateFilter === 'all' ? null : endDate,
        format,
        moduleName
      });
    }
  };

  // Reset form cuando se cierra
  const handleClose = () => {
    setStartDate('');
    setEndDate('');
    setFormat('pdf');
    setDateFilter('range');
    setErrors({});
    onClose();
  };

  // Establecer fecha de hoy como máximo
  const today = new Date().toISOString().split('T')[0];

  // Establecer fecha de hace 30 días como sugerencia
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const suggestedStartDate = thirtyDaysAgo.toISOString().split('T')[0];

  if (!isOpen) return null;

  return (
    <div className="download-report-modal-overlay" onClick={handleClose}>
      <div className="download-report-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-icon">
            <FiDownload size={24} />
          </div>
          <h2 className="modal-title">{title}</h2>
          <button
            className="modal-close-btn"
            onClick={handleClose}
            disabled={isLoading}
            type="button"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Rango de Fechas */}
          <div className="form-section">
            <h3 className="section-title">
              <FiCalendar size={18} />
              Seleccionar Período
            </h3>

            {/* Selector de tipo de filtro */}
            <div className="date-filter-selector">
              <label className="filter-option">
                <input
                  type="radio"
                  name="dateFilter"
                  value="range"
                  checked={dateFilter === 'range'}
                  onChange={(e) => {
                    setDateFilter(e.target.value);
                    setErrors({});
                  }}
                  disabled={isLoading}
                />
                <span className="filter-label">Por rango de fechas</span>
              </label>
              <label className="filter-option">
                <input
                  type="radio"
                  name="dateFilter"
                  value="all"
                  checked={dateFilter === 'all'}
                  onChange={(e) => {
                    setDateFilter(e.target.value);
                    setErrors({});
                  }}
                  disabled={isLoading}
                />
                <span className="filter-label">TODO (sin filtro de fecha)</span>
              </label>
            </div>

            {dateFilter === 'range' && (
              <div>
                <div className="date-range-group">
                  <div className="form-group">
                    <label className="form-label">
                      Fecha de Inicio <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-input ${errors.startDate ? 'error' : ''}`}
                      value={startDate}
                      onChange={(e) => {
                        setStartDate(e.target.value);
                        setErrors({ ...errors, startDate: null, dateRange: null });
                      }}
                      max={today}
                      disabled={isLoading}
                    />
                    {errors.startDate && (
                      <span className="form-error">{errors.startDate}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Fecha de Fin <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-input ${errors.endDate ? 'error' : ''}`}
                      value={endDate}
                      onChange={(e) => {
                        setEndDate(e.target.value);
                        setErrors({ ...errors, endDate: null, dateRange: null });
                      }}
                      max={today}
                      disabled={isLoading}
                    />
                    {errors.endDate && (
                      <span className="form-error">{errors.endDate}</span>
                    )}
                  </div>
                </div>

                {errors.dateRange && (
                  <div className="form-error-banner">{errors.dateRange}</div>
                )}

                {/* Sugerencias de fechas */}
                <div className="date-suggestions">
                  <span className="suggestion-label">Sugerencias:</span>
                  <button
                    type="button"
                    className="btn-suggestion"
                    onClick={() => {
                      setStartDate(suggestedStartDate);
                      setEndDate(today);
                      setErrors({});
                    }}
                    disabled={isLoading}
                  >
                    Últimos 30 días
                  </button>
                  <button
                    type="button"
                    className="btn-suggestion"
                    onClick={() => {
                      const firstDayOfMonth = new Date();
                      firstDayOfMonth.setDate(1);
                      setStartDate(firstDayOfMonth.toISOString().split('T')[0]);
                      setEndDate(today);
                      setErrors({});
                    }}
                    disabled={isLoading}
                  >
                    Este mes
                  </button>
                  <button
                    type="button"
                    className="btn-suggestion"
                    onClick={() => {
                      const firstDayOfYear = new Date();
                      firstDayOfYear.setMonth(0, 1);
                      setStartDate(firstDayOfYear.toISOString().split('T')[0]);
                      setEndDate(today);
                      setErrors({});
                    }}
                    disabled={isLoading}
                  >
                    Este año
                  </button>
                </div>
              </div>
            )}

            {dateFilter === 'all' && (
              <div className="all-records-info">
                <p>
                  Se descargarán <strong>TODOS</strong> los registros disponibles sin filtro de fecha.
                </p>
              </div>
            )}
          </div>

          {/* Formato de Descarga */}
          <div className="form-section">
            <h3 className="section-title">
              <FiFileText size={18} />
              Formato de Descarga
            </h3>

            <div className="format-options">
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="pdf"
                  checked={format === 'pdf'}
                  onChange={(e) => setFormat(e.target.value)}
                  disabled={isLoading}
                />
                <div className="format-card">
                  <div className="format-icon pdf">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="format-info">
                    <h4 className="format-name">PDF</h4>
                    <p className="format-description">Documento imprimible y profesional</p>
                  </div>
                </div>
              </label>

              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="csv"
                  checked={format === 'csv'}
                  onChange={(e) => setFormat(e.target.value)}
                  disabled={isLoading}
                />
                <div className="format-card">
                  <div className="format-icon csv">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="8" y1="13" x2="16" y2="13" stroke="currentColor" strokeWidth="2"/>
                      <line x1="8" y1="17" x2="16" y2="17" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                  <div className="format-info">
                    <h4 className="format-name">CSV</h4>
                    <p className="format-description">Para Excel y análisis de datos</p>
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            className="btn btn--outline"
            onClick={handleClose}
            disabled={isLoading}
            type="button"
          >
            Cancelar
          </button>
          <button
            className="btn btn--primary"
            onClick={handleDownload}
            disabled={isLoading}
            type="button"
          >
            {isLoading ? (
              <>
                <div className="spinner-sm"></div>
                Descargando...
              </>
            ) : (
              <>
                <FiDownload size={18} />
                Descargar {format.toUpperCase()}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DownloadReportModal;
